import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { getHost } from './config';
import { grantAnalyticsConsent } from './analytics';
import TermsAcceptanceModal from './TermsAcceptanceModal';

const PUBLIC_PATHS = new Set([
  '/',
  '/register',
  '/terms-of-use',
  '/privacy-policy',
  '/predict-methodology',
  '/forgot-username',
  '/forgot-password',
  '/reset-password',
]);

const TermsStatusContext = createContext({ needsTerms: false, checked: false });

// Lets descendants (e.g. MainPage's welcome-modal check) read the terms
// status this gate already fetched, instead of re-fetching it themselves.
export const useTermsStatus = () => useContext(TermsStatusContext);

export default function TermsAcceptanceGate({ children }) {
  const location = useLocation();
  const [needsTerms, setNeedsTerms] = useState(false);
  const [checked, setChecked] = useState(false);
  // Tracks which token we last fetched terms-status for, so navigating
  // between pages in the same session doesn't re-trigger the API call.
  const lastCheckedTokenRef = useRef(null);

  const checkTermsStatus = useCallback(async () => {
    const token = localStorage.getItem('authToken');

    if (!token || PUBLIC_PATHS.has(location.pathname)) {
      setChecked(true);
      return;
    }

    if (token === lastCheckedTokenRef.current) {
      // Already have a fresh result for this token; nothing changed.
      setChecked(true);
      return;
    }

    setChecked(false);
    try {
      const response = await fetch(`${getHost()}/api/terms-status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      lastCheckedTokenRef.current = token;

      if (response.status === 401 || !response.ok) {
        setNeedsTerms(false);
        setChecked(true);
        return;
      }

      const data = await response.json();
      setNeedsTerms(Boolean(data.needs_terms_acceptance));
      if (!data.needs_terms_acceptance) {
        grantAnalyticsConsent();
      }
    } catch {
      setNeedsTerms(false);
    } finally {
      setChecked(true);
    }
  }, [location.pathname]);

  useEffect(() => {
    checkTermsStatus();
  }, [checkTermsStatus]);

  const handleAccepted = () => {
    setNeedsTerms(false);
    if (localStorage.getItem('pendingWelcomeModal') === 'true') {
      localStorage.setItem('showWelcomeModal', 'true');
      localStorage.removeItem('pendingWelcomeModal');
      window.dispatchEvent(new CustomEvent('terms-accepted-show-welcome'));
    }
  };

  const showModal = needsTerms && !PUBLIC_PATHS.has(location.pathname);

  if (!checked && !PUBLIC_PATHS.has(location.pathname) && localStorage.getItem('authToken')) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <TermsStatusContext.Provider value={{ needsTerms, checked }}>
      {children}
      <TermsAcceptanceModal show={showModal} onAccepted={handleAccepted} />
    </TermsStatusContext.Provider>
  );
}
